import { querySuperuserContactSubmissions } from '@/features/contactSubmissions/data/contactSubmissions';
import { querySuperuserFeedback } from '@/features/feedback/data/feedback';
import { querySuperuserOrganisations } from '@/features/superuser/data/superuserOrganisations';
import { querySuperuserUsers } from '@/features/superuser/data/superuserUsers';
import type { SuperuserTabId } from '@/pages/superuserPageSupport';
import type {
  ContactSubmissionDto,
  FeedbackDto,
  SuperuserOrganisationDto,
  UserListItemDto,
} from '@flowqa/shared';

export type SuperuserNotificationTabId =
  | 'users'
  | 'organisations'
  | 'feedback'
  | 'contact-submissions';

export type SuperuserNotificationCounts = Record<SuperuserNotificationTabId, number>;

const latestRowsPageSize = 100;
const storageNamespace = 'flowqa-superuser-seen';

const superuserNotificationTabs: readonly SuperuserNotificationTabId[] = [
  'users',
  'organisations',
  'feedback',
  'contact-submissions',
];

const latestRowsQueryInput = {
  page: 1,
  pageSize: latestRowsPageSize,
  sorts: [{ field: 'createdAt', direction: 'desc' as const }],
  filters: [],
};

export const emptySuperuserNotificationCounts = (): SuperuserNotificationCounts => ({
  users: 0,
  organisations: 0,
  feedback: 0,
  'contact-submissions': 0,
});

export const isSuperuserNotificationTabId = (
  value: SuperuserTabId,
): value is SuperuserNotificationTabId =>
  superuserNotificationTabs.some((tabId) => tabId === value);

export const getSuperuserSeenStorageKey = (
  userId: string | null | undefined,
  tabId: SuperuserNotificationTabId,
): string => `${storageNamespace}:${userId ?? 'anonymous'}:${tabId}`;

export const readSuperuserSeenAt = (
  userId: string | null | undefined,
  tabId: SuperuserNotificationTabId,
): number | null => {
  if (typeof window === 'undefined') return null;
  const raw = window.localStorage.getItem(getSuperuserSeenStorageKey(userId, tabId));
  if (!raw) return null;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : null;
};

export const markSuperuserTabSeen = (
  userId: string | null | undefined,
  tabId: SuperuserNotificationTabId,
  seenAt = Date.now(),
): void => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(getSuperuserSeenStorageKey(userId, tabId), String(seenAt));
  window.dispatchEvent(new CustomEvent('flowqa-superuser-notifications-seen'));
};

const readCreatedAtTime = (value: Date | string): number => {
  if (value instanceof Date) return value.getTime();
  return Date.parse(value);
};

export const countRowsCreatedAfter = (
  rows: Array<{ createdAt: Date | string }>,
  seenAt: number | null,
): number => {
  if (seenAt === null) return 0;
  return rows.filter((row) => {
    const createdAt = readCreatedAtTime(row.createdAt);
    return Number.isFinite(createdAt) && createdAt > seenAt;
  }).length;
};

export const queryLatestSuperuserUsers = async (): Promise<UserListItemDto[]> => {
  const result = await querySuperuserUsers(latestRowsQueryInput);
  return result.items;
};

export const queryLatestSuperuserOrganisations = async (): Promise<SuperuserOrganisationDto[]> => {
  const result = await querySuperuserOrganisations(latestRowsQueryInput);
  return result.items;
};

export const queryLatestSuperuserFeedback = async (): Promise<FeedbackDto[]> => {
  const result = await querySuperuserFeedback(latestRowsQueryInput);
  return result.items;
};

export const queryLatestSuperuserContactSubmissions = async (): Promise<ContactSubmissionDto[]> => {
  const result = await querySuperuserContactSubmissions(latestRowsQueryInput);
  return result.items;
};
